import type { FormJourneyItem } from '@plura/shared/types';
import type { DesignVariant, VariantMediaPool } from './types';

/**
 * Assign images/videos from the supplied pool to each journey item that needs
 * one. We always set media on every supported step so the variant feels
 * consistent (rather than a half-decorated mix).
 *
 * Items whose type is text-only by nature (e.g. CONTACT_INFO is fine with media,
 * but ADDRESS often is too) all receive media — only OPTION_SCALE/RATING/NPS
 * stay flexible since the layout engine centers them on a backdrop anyway.
 *
 * MULTIPLE_CHOICE option-level media is intentionally untouched in v1.
 */
export function assignVisuals(
  journeyItems: FormJourneyItem[],
  pool: VariantMediaPool,
  variant: DesignVariant,
): FormJourneyItem[] {
  if (pool.images.length === 0 && pool.videos.length === 0) {
    return journeyItems;
  }

  let imageCursor = 0;
  let videoCursor = 0;

  const nextImage = () => {
    if (pool.images.length === 0) return null;
    const m = pool.images[imageCursor % pool.images.length];
    imageCursor += 1;
    return m;
  };
  const nextVideo = () => {
    if (pool.videos.length === 0) return null;
    const m = pool.videos[videoCursor % pool.videos.length];
    videoCursor += 1;
    return m;
  };

  return journeyItems.map((item, index) => {
    const isWelcome = item.type === 'WELCOME_SCREEN';
    const isEnd = item.type === 'END_SCREEN' || item.type === 'REDIRECT_SCREEN';

    let media = item.media;

    if (isWelcome) {
      media = (variant.preferWelcomeVideo ? nextVideo() : null) ?? nextImage() ?? media;
    } else if (isEnd) {
      media = nextImage() ?? media;
    } else {
      // Sprinkle a single video into the body of the form for variety.
      const useVideo = variant.preferWelcomeVideo && index > 0 && index === Math.min(3, journeyItems.length - 2);
      media = (useVideo ? nextVideo() : null) ?? nextImage() ?? media;
    }

    if (!media) return item;
    return { ...item, media };
  });
}
